import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { Progress } from "../../../client/components/ui/progress";
import { useChallengeOverviewStats } from "./useChallengeOverviewStats";
import { usePfmData } from "../../../prop-firm-manager/PfmDataContext";

export default function PropFirmChallengeOverviewWidget() {
  const { t } = usePfmData();
  const { totalAccounts, accountsWithPayout, ratio } = useChallengeOverviewStats();
  const containerRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setCompact(entry.contentRect.height < 190);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (compact) {
    return (
      <div ref={containerRef} className="h-full">
        <Card className="h-full flex flex-col">
          <CardSectionHeader label={t.dashboard.widgets.propfirmChallengeOverview} isDark={false} compact />
          <CardContent className="p-3 flex flex-col justify-center flex-1 gap-1.5">
            <p className="text-[10px] font-semibold tracking-widest uppercase widget-label truncate">
              {t.propFirmManager.totalAccounts}
            </p>
            <div className="flex items-baseline gap-3">
              <span className="text-2xl font-bold font-mono tabular-nums leading-none">
                {totalAccounts}
              </span>
              <span className="text-sm text-muted-foreground font-mono tabular-nums">
                {accountsWithPayout}{" "}
                <span className="text-[11px]">{t.propFirmManager.accountsWithPayout}</span>
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-base font-bold font-mono tabular-nums leading-none text-primary">
                {ratio.toFixed(1)}%
              </span>
              <span className="text-[10px] text-muted-foreground font-semibold tracking-widest uppercase widget-label">
                {t.propFirmManager.successRate}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full">
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.dashboard.widgets.propfirmChallengeOverview} isDark={false} compact />
        <CardContent className="flex-1 min-h-0 p-3 flex flex-col gap-2 overflow-hidden">
          <div className="grid grid-cols-2 gap-2 flex-shrink-0">
            <Card>
              <CardContent className="p-3 flex flex-col justify-center">
                <p className="text-[10px] font-semibold tracking-widest uppercase widget-label mb-1 truncate">
                  {t.propFirmManager.totalAccounts}
                </p>
                <p className="text-xl font-bold font-mono tabular-nums leading-none">
                  {totalAccounts}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 flex flex-col justify-center">
                <p className="text-[10px] font-semibold tracking-widest uppercase widget-label mb-1 truncate">
                  {t.propFirmManager.accountsWithPayout}
                </p>
                <p className="text-xl font-bold font-mono tabular-nums leading-none text-primary">
                  {accountsWithPayout}
                </p>
              </CardContent>
            </Card>
          </div>
          <Card className="flex-1 min-h-0">
            <CardContent className="p-3 flex flex-col justify-center h-full">
              <p className="text-[10px] font-semibold tracking-widest uppercase widget-label mb-2 truncate">
                {t.propFirmManager.successRate}
              </p>
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-2xl font-bold font-mono tabular-nums leading-none text-primary">
                  {ratio.toFixed(1)}%
                </span>
                <span className="text-xs text-muted-foreground font-mono tabular-nums">
                  {accountsWithPayout}/{totalAccounts}
                </span>
              </div>
              <Progress value={ratio} className="h-2" />
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}
